import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withModifiers as _withModifiers, unref as _unref, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["value"]
const _hoisted_2 = { class: "space-y-4" }
const _hoisted_3 = { class: "flex items-center justify-between" }
const _hoisted_4 = { class: "text-sm text-black" }
const _hoisted_5 = { class: "grid grid-cols-6 gap-3" }
const _hoisted_6 = ["for"]
const _hoisted_7 = ["id", "onUpdate:modelValue", "onBlur"]
const _hoisted_8 = {
  key: 0,
  class: "text-sm text-red-600"
}
const _hoisted_9 = { class: "flex justify-end space-x-3 pt-4 border-t border-gray-200" }
const _hoisted_10 = ["disabled"]
const _hoisted_11 = {
  key: 0,
  class: "fas fa-spinner fa-spin mr-2"
}
const _hoisted_12 = {
  key: 1,
  class: "fas fa-save mr-2"
}

import { ref, reactive, watch, computed } from 'vue'
import { ModalComp } from '@/components'
import type { Pack } from '@/types'

interface Props {
  isVisible: boolean
  pack: Pack | null
}

interface Emits {
  (e: 'update:is-visible', value: boolean): void
  (e: 'submit', pack: Pack): void
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PackageEditModal',
  props: {
    isVisible: { type: Boolean },
    pack: {}
  },
  emits: ["update:is-visible", "submit"],
  setup(__props: any, { emit: __emit }) {

const props = __props
const emit = __emit

const isSubmitting = ref(false)

const formData = reactive({
  id_pack: 0,
  id_vendeur: 0,
  titre: '',
  nombreTailles: 1,
})

const errors = reactive({
  titre: '',
  quantites: ''
})

// Colonnes de quantités réactives
const quantityColumns = ref<number[]>([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0])
const initialQuantityColumns = ref<number[]>([])
const initialNombreTailles = ref(1)
const visibleQuantityColumns = computed(() => quantityColumns.value.slice(0, formData.nombreTailles))
const legacyQuantitiesAreUnchanged = computed(() => {
  return formData.nombreTailles === initialNombreTailles.value
    && quantityColumns.value.every((quantity, index) => quantity === initialQuantityColumns.value[index])
})

// Vérifier si le formulaire est valide
const isFormValid = computed(() => {
  const quantitiesAreStrictlyValid = quantityColumns.value
    .slice(0, formData.nombreTailles)
    .every(qty => Number.isInteger(qty) && qty > 0)
  return quantitiesAreStrictlyValid || legacyQuantitiesAreUnchanged.value
})

// Fonction pour nettoyer une quantité quand l'utilisateur quitte le champ
const cleanQuantity = (index: number) => {
  const value = quantityColumns.value[index]
  if (value === null || value === undefined || isNaN(Number(value))) {
    quantityColumns.value[index] = 0
  } else {
    quantityColumns.value[index] = Math.max(0, Number(value)) // S'assurer que c'est >= 0
  }
}

// Initialiser le formulaire quand le pack change
watch(() => props.pack, (newPack) => {
  if (newPack) {
    formData.id_pack = newPack.id_pack
    formData.id_vendeur = newPack.id_vendeur
    formData.titre = newPack.titre || ''
    const quantities = [
      newPack.p1, newPack.p2, newPack.p3, newPack.p4, newPack.p5, newPack.p6,
      newPack.p7, newPack.p8, newPack.p9, newPack.p10, newPack.p11, newPack.p12,
    ]
    formData.nombreTailles = quantities.reduce(
      (lastIndex, quantity, index) => quantity > 0 ? index + 1 : lastIndex,
      1
    )
    initialNombreTailles.value = formData.nombreTailles
    initialQuantityColumns.value = [...quantities]
    
    // Mettre à jour les colonnes de quantités
    quantityColumns.value = [
      newPack.p1,
      newPack.p2,
      newPack.p3,
      newPack.p4,
      newPack.p5,
      newPack.p6,
      newPack.p7,
      newPack.p8,
      newPack.p9,
      newPack.p10,
      newPack.p11,
      newPack.p12,
    ]
  }
}, { immediate: true })

const validateForm = () => {
  errors.titre = ''
  errors.quantites = ''
  
  const hasInvalidQuantity = quantityColumns.value
    .slice(0, formData.nombreTailles)
    .some(qty => !Number.isInteger(qty) || qty <= 0)
  if (hasInvalidQuantity && !legacyQuantitiesAreUnchanged.value) {
    errors.quantites = 'Toutes les quantités doivent être des entiers strictement positifs'
    return false
  }
  
  return true
}

const handleSubmit = async () => {
  if (!validateForm()) return

  isSubmitting.value = true
  
  try {
    // Nettoyer les valeurs : convertir les valeurs vides/null/undefined en 0
    const cleanQuantities = quantityColumns.value.map(qty => {
      if (qty === null || qty === undefined || isNaN(Number(qty))) {
        return 0
      }
      return Number(qty)
    })
    
    const packToUpdate: Pack = {
      id_pack: formData.id_pack,
      id_vendeur: formData.id_vendeur,
      titre: formData.titre || null,
      p1: formData.nombreTailles >= 1 ? cleanQuantities[0] : 0,
      p2: formData.nombreTailles >= 2 ? cleanQuantities[1] : 0,
      p3: formData.nombreTailles >= 3 ? cleanQuantities[2] : 0,
      p4: formData.nombreTailles >= 4 ? cleanQuantities[3] : 0,
      p5: formData.nombreTailles >= 5 ? cleanQuantities[4] : 0,
      p6: formData.nombreTailles >= 6 ? cleanQuantities[5] : 0,
      p7: formData.nombreTailles >= 7 ? cleanQuantities[6] : 0,
      p8: formData.nombreTailles >= 8 ? cleanQuantities[7] : 0,
      p9: formData.nombreTailles >= 9 ? cleanQuantities[8] : 0,
      p10: formData.nombreTailles >= 10 ? cleanQuantities[9] : 0,
      p11: formData.nombreTailles >= 11 ? cleanQuantities[10] : 0,
      p12: formData.nombreTailles >= 12 ? cleanQuantities[11] : 0,
    }
    
    emit('submit', packToUpdate)
  } catch (error) {
    console.error('Erreur lors de la modification:', error)
  } finally {
    isSubmitting.value = false
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(ModalComp), {
    "is-visible": _ctx.isVisible,
    "onUpdate:isVisible": _cache[3] || (_cache[3] = ($event: any) => (_ctx.$emit('update:is-visible', $event))),
    title: "Modifier le paquet",
    size: "lg",
    "hide-footer": true
  }, {
    body: _withCtx(() => [
      _createElementVNode("form", {
        onSubmit: _withModifiers(handleSubmit, ["prevent"]),
        class: "space-y-6"
      }, [
        _createElementVNode("div", null, [
          _cache[4] || (_cache[4] = _createElementVNode("label", {
            for: "edit-titre",
            class: "block text-sm font-medium text-black mb-2"
          }, " Nom du paquet ", -1)),
          _withDirectives(_createElementVNode("input", {
            id: "edit-titre",
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formData.titre) = $event)),
            type: "text",
            placeholder: "Ex: Pack Standard, 1-1-1-1-1 (optionnel)",
            class: "w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          }, null, 512), [
            [_vModelText, formData.titre]
          ])
        ]),
        _createElementVNode("div", null, [
          _cache[5] || (_cache[5] = _createElementVNode("label", {
            for: "edit-nombre-tailles",
            class: "block text-sm font-medium text-black mb-2"
          }, " Nombre de tailles du paquet ", -1)),
          _withDirectives(_createElementVNode("select", {
            id: "edit-nombre-tailles",
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formData.nombreTailles) = $event)),
            class: "w-full px-3 py-2 border border-gray-300 rounded-md"
          }, [
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(12, (nombreTailles) => {
              return _createElementVNode("option", {
                key: nombreTailles,
                value: nombreTailles
              }, _toDisplayString(nombreTailles), 9, _hoisted_1)
            }), 64))
          ], 512), [
            [
              _vModelSelect,
              formData.nombreTailles,
              void 0,
              { number: true }
            ]
          ])
        ]),
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _cache[6] || (_cache[6] = _createElementVNode("label", { class: "block text-sm font-medium text-black" }, " Quantités par taille ", -1)),
            _createElementVNode("div", _hoisted_4, " Colonnes p1 à p" + _toDisplayString(formData.nombreTailles), 1)
          ]),
          _createElementVNode("div", _hoisted_5, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleQuantityColumns.value, (quantite, index) => {
              return (_openBlock(), _createElementBlock("div", {
                key: index,
                class: "text-center"
              }, [
                _createElementVNode("label", {
                  for: `p${index + 1}`,
                  class: "block text-xs font-medium text-black mb-1"
                }, " p" + _toDisplayString(index + 1), 9, _hoisted_6),
                _createElementVNode("div", {
                  class: _normalizeClass(["px-2 py-1 rounded-md border", quantite > 0 ? 'bg-blue-50 border-blue-300' : 'bg-gray-50 border-gray-200'])
                }, [
                  _withDirectives(_createElementVNode("input", {
                    id: `p${index + 1}`,
                    "onUpdate:modelValue": ($event: any) => ((quantityColumns.value[index]) = $event),
                    type: "number",
                    min: "1",
                    step: "1",
                    class: _normalizeClass(["w-full text-center bg-transparent border-none outline-none text-sm font-medium", quantite > 0 ? 'text-blue-700' : 'text-black']),
                    onBlur: ($event: any) => (cleanQuantity(index))
                  }, null, 42, _hoisted_7), [
                    [
                      _vModelText,
                      quantityColumns.value[index],
                      void 0,
                      { number: true }
                    ]
                  ])
                ], 2)
              ]))
            }), 128))
          ]),
          (errors.quantites)
            ? (_openBlock(), _createElementBlock("p", _hoisted_8, _toDisplayString(errors.quantites), 1))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_9, [
          _createElementVNode("button", {
            type: "button",
            onClick: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('update:is-visible', false))),
            class: "px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-black bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          }, " Annuler "),
          _createElementVNode("button", {
            type: "submit",
            disabled: isSubmitting.value || !isFormValid.value,
            class: "px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          }, [
            (isSubmitting.value)
              ? (_openBlock(), _createElementBlock("i", _hoisted_11))
              : (_openBlock(), _createElementBlock("i", _hoisted_12)),
            _createTextVNode(" " + _toDisplayString(isSubmitting.value ? 'Sauvegarde...' : 'Sauvegarder les modifications'), 1)
          ], 8, _hoisted_10)
        ])
      ], 32)
    ]),
    _: 1
  }, 8, ["is-visible"]))
}
}

})